import { placeholderPayload } from '@components/AccountAllocationGrid/placeholderMatrix';
import type { AccountAllocationGridReport } from '@components/AccountAllocationGrid/types';
import {
  allocationCellKey,
  parseAllocationCellKey,
} from '@ovComponents/4-module/widgets/portfolio/components/allocationCellKey';
import type { SubAccountCustomAllocationInput } from '@ovComponents/4-module/widgets/portfolio/components/buildCustomAllocationInput';
import { tradeableAllocationSubAccounts } from '@ovComponents/4-module/widgets/portfolio/components/tradeableAllocationMembers';

// The context is posted whole on every change, so each id is written once and
// cells are bare numbers to keep the payload small.
export type StagedAllocationGrid = {
  subAccountIds: string[];
  financialProductIds: string[];
  fractions: (number | null)[][];
};

export type StagedAccountAllocations = {
  householdClientGroupId: string;
  portfolioReportId: string;
  matrix: StagedAllocationGrid;
  lockedCells: [row: number, column: number][];
  // Optional: completions staged before uploads were recorded don't have it.
  uploadedCells?: [row: number, column: number][];
  stagedAt: string;
};

export type StagedAccountAllocationBaseline = {
  portfolioReportId: string;
  fingerprint: string;
  capturedAt: string;
};

type StageAccountAllocationsInput = {
  householdClientGroupId: string;
  report: AccountAllocationGridReport;
  matrix: SubAccountCustomAllocationInput[];
  lockedCells: Iterable<string>;
  uploadedCells: Iterable<string>;
  stagedAt: Date;
};

const toGrid = (
  matrix: SubAccountCustomAllocationInput[],
): StagedAllocationGrid => {
  const financialProductIds = [
    ...new Set(
      matrix.flatMap(({ overrides }) =>
        overrides.map(({ financialProductId }) => financialProductId),
      ),
    ),
  ];
  const columnByFinancialProductId = new Map(
    financialProductIds.map((financialProductId, column) => [
      financialProductId,
      column,
    ]),
  );

  return {
    subAccountIds: matrix.map(({ subAccountId }) => subAccountId),
    financialProductIds,
    fractions: matrix.map(({ overrides }) => {
      const row: (number | null)[] = financialProductIds.map(() => null);
      for (const {
        financialProductId,
        customExpectedPercentage,
      } of overrides) {
        const column = columnByFinancialProductId.get(financialProductId);
        if (column !== undefined) row[column] = customExpectedPercentage;
      }
      return row;
    }),
  };
};

const toGridPosition = (
  grid: StagedAllocationGrid,
  key: string,
): [row: number, column: number] => {
  const { subAccountId, financialProductId } = parseAllocationCellKey(key);
  const row = grid.subAccountIds.indexOf(subAccountId);
  const column = grid.financialProductIds.indexOf(financialProductId);
  if (row === -1 || column === -1 || grid.fractions[row][column] === null) {
    throw new Error(`Cannot mark ${key}: it is not a staged cell`);
  }
  return [row, column];
};

const compareIds = (left: string, right: string) =>
  Number(left > right) - Number(left < right);

const fnv1a = (text: string) => {
  let hash = 0x811c9dc5;
  for (let index = 0; index < text.length; index++) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
};

export const stagedAccountAllocations = ({
  householdClientGroupId,
  report,
  matrix,
  lockedCells,
  uploadedCells,
  stagedAt,
}: StageAccountAllocationsInput) => {
  const grid = toGrid(matrix);

  return {
    householdClientGroupId,
    portfolioReportId: report.id,
    matrix: grid,
    lockedCells: [...lockedCells].map((key) => toGridPosition(grid, key)),
    uploadedCells: [...uploadedCells].map((key) => toGridPosition(grid, key)),
    stagedAt: stagedAt.toISOString(),
  } satisfies StagedAccountAllocations;
};

const toCellKeys = (
  grid: StagedAllocationGrid,
  positions: [row: number, column: number][],
) =>
  positions.map(([row, column]) =>
    allocationCellKey(
      grid.subAccountIds[row],
      grid.financialProductIds[column],
    ),
  );

export const unpackStagedAccountAllocations = ({
  matrix,
  lockedCells,
  uploadedCells = [],
}: Pick<
  StagedAccountAllocations,
  'matrix' | 'lockedCells' | 'uploadedCells'
>) => ({
  matrix: matrix.subAccountIds.map(
    (subAccountId, row): SubAccountCustomAllocationInput => ({
      subAccountId,
      overrides: matrix.financialProductIds.flatMap(
        (financialProductId, column) => {
          const customExpectedPercentage = matrix.fractions[row][column];
          return customExpectedPercentage === null
            ? []
            : [{ financialProductId, customExpectedPercentage }];
        },
      ),
    }),
  ),
  lockedCells: toCellKeys(matrix, lockedCells),
  uploadedCells: toCellKeys(matrix, uploadedCells),
});

// Sorted first: the report's order is not guaranteed between fetches, and a
// reorder must not read as the allocation having moved.
export const accountAllocationFingerprint = (
  report: AccountAllocationGridReport,
) =>
  fnv1a(
    JSON.stringify(
      [...placeholderPayload(report)]
        .sort((left, right) =>
          compareIds(left.subAccountId, right.subAccountId),
        )
        .map(({ subAccountId, overrides }) => [
          subAccountId,
          [...overrides]
            .sort((left, right) =>
              compareIds(left.financialProductId, right.financialProductId),
            )
            .map(({ financialProductId, customExpectedPercentage }) => [
              financialProductId,
              customExpectedPercentage,
            ]),
        ]),
    ),
  );

export const stagedAccountAllocationBaseline = (
  report: AccountAllocationGridReport,
  capturedAt: Date,
) =>
  ({
    portfolioReportId: report.id,
    fingerprint: accountAllocationFingerprint(report),
    capturedAt: capturedAt.toISOString(),
  }) satisfies StagedAccountAllocationBaseline;

// Shared by the review and apply steps: a staged matrix only fits the accounts
// it was staged for, so a tradeable account joining or leaving blocks both.
export const stagedMembershipChange = (
  staged: Pick<StagedAccountAllocations, 'matrix'>,
  report: AccountAllocationGridReport,
) => {
  const stagedIds = new Set(staged.matrix.subAccountIds);
  const reportIds = new Set(
    tradeableAllocationSubAccounts(report.subAccounts).map(
      (account) => account.subAccount.id,
    ),
  );
  const added = [...reportIds].filter((id) => !stagedIds.has(id));
  const removed = [...stagedIds].filter((id) => !reportIds.has(id));
  return added.length > 0 || removed.length > 0 ? { added, removed } : null;
};
